import { inject, Injectable } from "@angular/core";
import { IMapPointClick } from "@models/map/map.model";
import * as L from "leaflet";
import "leaflet.markercluster";
import { DrawMode, ILatLng, PolyDrawService } from "polydraw";
import { delay, takeUntil } from "rxjs";
import { EstateClusterMapMarkerComponent } from "src/app/map/map-marker/estate/estate-cluster-map-marker.component";
import { EstateClusterPopupComponent } from "src/app/map/map-marker/estate/estate-cluster-popup.component";
import { EstateMapMarkerComponent } from "src/app/map/map-marker/estate/estate-map-marker.component";
import { IResidenceSaleInArea, SaleInArea } from "src/models/estate/IResidenceSaleInArea";
import { ISalePositionInArea } from "src/models/estate/ISalePositionsInArea";
import { DistanceLineService } from "../map/distance-line.service";
import { BaseMapComponentsService, IMarkerClusterParams } from "../MapComponentsBaseService";
import { MapMode } from "../map/map-state.service";

@Injectable()
export class EstateMapComponentsService extends BaseMapComponentsService {
    private readonly mapHelperService = inject(PolyDrawService);
    private readonly distanceLineService = inject(DistanceLineService)
    currentDrawMode: DrawMode;
    constructor() {
        super();     
        this.mapActionsService.onMapClicked$.pipe(takeUntil(this.destroy$)).subscribe((click) => this.onMarketSalesMapClicked(click));

        this.mapHelperService.drawMode$.pipe(delay(100), takeUntil(this.destroy$)).subscribe((mode: DrawMode) => (this.currentDrawMode = mode));
    }
    updateLayers(residenceSalesInArea: IResidenceSaleInArea) {
        if (this.layer) {
            this.layer.clearLayers();
        }

        if (residenceSalesInArea.sales == null) return;

        if (this.map) {
            // Destroy the previous draw's marker/popup views right before rebuilding, so they don't
            // accumulate across redraws. Kept here (not on the early-return path) so a null response
            // leaves the currently visible markers untouched.
            this.clearTrackedComponents();
            this.layer = L.layerGroup().addTo(this.map);
            // if (sales.simplifiedSearch) {
            //     this.createOverviewMarkers(sales.data);
            // } else {
            this.createSaleMarkers(residenceSalesInArea.sales);
            // }
        }
    }

    updateOverviewLayers(salePositionsInArea: ISalePositionInArea[]) {
        if (this.layer) {
            this.layer.clearLayers();
        }

        if (salePositionsInArea == null) return;

        if (this.map) {
            // Release any detailed sale-marker views when switching to the overview layer.
            this.clearTrackedComponents();
            this.layer = L.layerGroup().addTo(this.map);
            // if (sales.simplifiedSearch) {
            this.createOverviewMarkers(salePositionsInArea);
        }
    }

    private onMarketSalesMapClicked(click: IMapPointClick): void {
        const selectIsActive: boolean = this.mapStateService.getCurrentAnyEstateActive();
        if (click.zoom > this.mapStateService.zoomLimitClickAny && selectIsActive && this.currentDrawMode === DrawMode.Off && this.mapStateService.mapModeSignal() === MapMode.Market2) {
            this.mapDataService.getNearestMarketSaleUnits(click.lat, click.lng).subscribe((units) => {
                if (units.length > 0) {
                    this.openPopupForCluster(click, units);
                }
            });
        }
    }
    private createOverviewMarker(sale: ISalePositionInArea) {
        const icon = L.divIcon({
            html: "<div></div>",
            className: "overview-marker",
            iconSize: [30, 30],
        });
        const marker = L.marker([sale.latitude, sale.longitude], { icon: icon });
        (marker as any).originalObject = sale;
        return marker;
    }

    private createOverviewMarkers(sales: ISalePositionInArea[]): void {
        const estateMarkers: any[] = [];

        sales.forEach((item) => {
            if (item.latitude != null && item.longitude != null) {
                const marker: any = this.createOverviewMarker(item);
                estateMarkers.push(marker);
            }
        });

        this.createOverviewClusterGroup({
            className: "overview-marker",
            iconSize: [30, 30],
            markers: estateMarkers,
            targetEventListener: (e, t) => this.bindMarkerClusterEvents(e, t),
            targetEvents: ["clusterclick", "clustermouseover", "clustermouseout"],
            chunkedLoading: true,
            maxClusterRadius: 110,
            spiderfyOnMaxZoom: false,
            zoomToBoundsOnClick: true,
        });
    }
    private createEstateMarkerElement(sale: SaleInArea): HTMLElement {
        // Marker content is static once built, so keep it out of the global CD loop:
        // create detached and render once.
        const [element, component] = this.createElementWithComponent(EstateMapMarkerComponent, "div", false);
        component.setInput("data", sale);
        component.changeDetectorRef.detectChanges();
        return element;
    }

    private createEstateMarker(sale: SaleInArea): L.Marker {
        const htmlElement = this.createEstateMarkerElement(sale);
        const className = "estate-marker";
        const lat = sale.latitude;
        const lng = sale.longitude;
        const originalObject = sale;
        const targetEventListener = (e: any, t: any) => this.bindEstateMarkerEvents(e, t);
        const targetEvents = ["click", "mouseover", "mouseout"];
        return this.createMarker(lat, lng, htmlElement, className, originalObject, targetEvents, targetEventListener);
    }

    private createSaleMarkers(sales: SaleInArea[]): void {
        // The whole marker build + clustering is pure Leaflet/DOM work; keep it off the
        // Angular zone so it doesn't trigger a change-detection storm while drawing.
        this.ngZone.runOutsideAngular(() => {
            const estateMarkers: any[] = [];

            sales.forEach((sale) => {
                if (sale.latitude != null && sale.longitude != null) {
                    const marker: L.Marker = this.createEstateMarker(sale);

                    // Bind the popup lazily: the content component is only built the first
                    // time this specific marker is clicked, not eagerly for every sale.
                    marker.bindPopup(this.createEstateMarkerPopup(sale));

                    estateMarkers.push(marker);
                }
            });

            const markerClusterParams: IMarkerClusterParams = {
                className: "map-cluster-wrapper",
                iconSize: [116, 22],
                markers: estateMarkers,
                targetEventListener: (e, t) => this.bindMarkerClusterEvents(e, t),
                targetEvents: ["clusterclick", "clustermouseover", "clustermouseout"],
                chunkedLoading: true,
                maxClusterRadius: 110,
                spiderfyOnMaxZoom: false,
                zoomToBoundsOnClick: false,
            };
            this.createMarkerCluster(this.createMarkerClusterGroup(markerClusterParams), markerClusterParams);
        });
    }

    
    bindMarkerClusterEvents(event: any, eventType: string): void {
        switch (eventType) {
        
            case "clustermouseover": {
                const lat: number = Number(event.latlng.lat);
                const lng: number = Number(event.latlng.lng);
                const latlng: ILatLng = { lat, lng };
                this.distanceLineService.showDistanceLine(latlng);
                break;
            }
            case "clustermouseout": {
                this.distanceLineService.removeDistanceLine();
                break;
            }
        }
    }

       private bindEstateMarkerEvents(event: any, eventType: string): void {
        switch (eventType) {
            case "mouseover": {
                const lat: number = Number(event.latlng.lat);
                const lng: number = Number(event.latlng.lng);
                const latlng: ILatLng = { lat, lng };
                this.distanceLineService.showDistanceLine(latlng);
                break;
            }
            case "mouseout": {
                this.distanceLineService.removeDistanceLine();
                break;
            }
        }
    }

    public createEstateClusterElement(sales: SaleInArea[]): HTMLElement {
        // Static cluster label; render once and keep it out of the global CD loop.
        const [element, component] = this.createElementWithComponent(EstateClusterMapMarkerComponent, "div", false);
        component.setInput("data", sales);
        component.changeDetectorRef.detectChanges();
        return element;
    }
    private createMarkerClusterGroup(markerClusterParams: IMarkerClusterParams): L.MarkerClusterGroup {
        let sales: SaleInArea[] = [];
        let leaflet = this.getMarkerClusterGroup();

        const markerCluster = leaflet.markerClusterGroup({
            iconCreateFunction: (cluster: any) => {
                const markers = cluster.getAllChildMarkers();
                markers.forEach((m: any) => {
                    sales.push((m as any).originalObject);
                });
                const html = this.createEstateClusterElement(sales);
                sales = [];
                return (L as any).divIcon({
                    iconSize: markerClusterParams.iconSize,
                    html: html,
                    className: markerClusterParams.className,
                    iconAnchor: [15, 15],
                });
            },
            showCoverageOnHover: true,
            polygonOptions: {
                color: "#3d91c6",
                weight: 2,
            },
            spiderfyOnMaxZoom: markerClusterParams.spiderfyOnMaxZoom,
            chunkedLoading: markerClusterParams.chunkedLoading,
            zoomToBoundsOnClick: markerClusterParams.zoomToBoundsOnClick,
            maxClusterRadius: () => {
                return markerClusterParams.maxClusterRadius;
            },
        });
        return markerCluster;
    }
    protected override openPopupForCluster(position: { lat: number; lng: number }, sales: any[]): void {
        const [htmlElement, component] = this.createElementWithComponent(EstateClusterPopupComponent);
        component.setInput("data", sales);
              component.instance.closeClicked
            .asObservable()
            .subscribe(() => {
                this.closePopup();
            });
        this.openPopupWithSales(htmlElement, sales?.length, position);
    }

    private createEstateMarkerPopup(sale: SaleInArea): L.Popup {
        // Build the popup content lazily — only when the marker is actually opened.
        // The popup is interactive (sortable list), so it stays attached to the CD loop.
        return this.createMarkerPopup(() => {
            const [htmlElement, component] = this.createElementWithComponent(EstateClusterPopupComponent);
            component.setInput("data", [sale]);
            component.changeDetectorRef.detectChanges();
               component.instance.closeClicked
            .asObservable()
            .subscribe(() => {
                this.closePopup();
            });
            return htmlElement;
        }, "cluster-popup");
    }
}
